import React, { createContext, useContext, useEffect, useState } from 'react';

const CACHE_KEY = 'seoul-store-settings-v2';

const DEFAULT = {
  payment_methods: [],
  shipping: { cost: 200 },
  whatsapp_number: '',
  topBanner:  { enabled: false, image_url: '', link: '', height: 36 },
  popularCategories: [],
  categoryStrip: [],
  homepageCategories: [],
  social: { facebook: '', instagram: '', instagram_handle: '@seoulstation' },
  logo:       { text: 'Seoul', dot: 'Station', tagline: 'Premium Korean Store', color: '#111111', accent_color: '#c0392b' },
  ticker:     { enabled: true, messages: ['Free Shipping on orders over Rs 2000', 'New Arrivals Every Week', 'Secure & Encrypted Checkout', 'Free 30-Day Returns'], bg_color: '#fefce8', text_color: '#92400e' },
  hero:       { tag: '✦ New arrivals every week', title: 'Shop the <em>future</em> of online retail', subtitle: 'Discover curated collections across electronics, fashion, and home — all in one place, delivered fast.', btn1_text: 'Shop Now', btn1_link: '/products', btn2_text: 'Browse Categories', btn2_link: '/products', stat1: { value: '50k+', label: 'Products' }, stat2: { value: '200k+', label: 'Customers' }, stat3: { value: '4.9★', label: 'Avg Rating' } },
  categories: { enabled: true, label: 'Collections', title: 'Shop by Category', subtitle: 'Explore our handpicked selections — new categories added every month' },
  featured:   { enabled: true, label: 'Featured',    title: 'Trending This Week', subtitle: 'Our most popular products, loved by thousands of customers' },
  staffFavorites: { enabled: true, title: 'Staff Favorites', subtitle: 'Most liked by us', products: [] },
  newArrivals:    { enabled: true, title: 'New Arrivals', subtitle: 'Discover something New', products: [] },
  carouselCats: [],
  featuredCats: [
    { name: 'Noodles',    image_url: 'https://images.unsplash.com/photo-1626082927389-6cd097cda826?w=500&h=500&fit=crop', link: '/products' },
    { name: 'Sauce',      image_url: 'https://images.unsplash.com/photo-1596040587171-a32ce2c96b15?w=500&h=500&fit=crop', link: '/products' },
    { name: 'Chopsticks', image_url: 'https://images.unsplash.com/photo-1610701596007-11502861dcfa?w=500&h=500&fit=crop', link: '/products' },
  ],
  navLinks: [
    { label: 'Samyang',     to: '/products', dropdown: true },
    { label: 'Nongshim',    to: '/products', dropdown: true },
    { label: 'Sauces',      to: '/products'                 },
    { label: 'Chopsticks',  to: '/products'                 },
    { label: 'Tteokbokki',  to: '/products'                 },
    { label: 'Sushi Sheet', to: '/products'                 },
    { label: 'Rice Paper',  to: '/products'                 },
    { label: 'Track Order', to: '/orders'                   },
  ],
};

const Ctx = createContext({ settings: DEFAULT, editMode: false });

export function StoreSettingsProvider({ children }) {
  const [settings, setSettings] = useState(() => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      return cached ? JSON.parse(cached) : DEFAULT;
    } catch { return DEFAULT; }
  });
  // Auto-enter edit mode when page is loaded inside admin iframe
  const [editMode, setEditMode] = useState(() => typeof window !== 'undefined' && window.parent !== window);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1'}/store/settings`)
      .then(r => r.json())
      .then(d => {
        if (d.settings) {
          setSettings(d.settings);
          try { localStorage.setItem(CACHE_KEY, JSON.stringify(d.settings)); } catch {}
        }
      })
      .catch(() => {});

    const handle = (e) => {
      if (e.data?.type === 'PREVIEW_UPDATE')  setSettings(e.data.settings);
      if (e.data?.type === 'ENTER_EDIT_MODE') setEditMode(true);
      if (e.data?.type === 'EXIT_EDIT_MODE')  setEditMode(false);
    };
    window.addEventListener('message', handle);
    return () => window.removeEventListener('message', handle);
  }, []);

  // In edit mode (inside the iframe), block all link navigation so clicks
  // stay on the page and still bubble up to section click handlers.
  useEffect(() => {
    if (!editMode) return;
    const blockNav = (e) => {
      const a = e.target.closest('a, button[type="submit"]');
      if (a) e.preventDefault();
    };
    document.addEventListener('click', blockNav, true);
    return () => document.removeEventListener('click', blockNav, true);
  }, [editMode]);

  return (
    <Ctx.Provider value={{ settings, editMode }}>
      {children}
    </Ctx.Provider>
  );
}

export const useStoreSettings = () => useContext(Ctx);
